'use client';
import React, { createRef } from 'react';
import classNames from 'classnames';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { useHotkeys } from 'react-hotkeys-hook';
import { getNotificationsStore } from '../store/notifications-store';
import { Notification, NotificationType } from '../types';
import XIcon from '@/common/components/common/svg/XIcon';
import ErrorIcon from '@/common/components/icons/ErrorIcon';
import CheckIcon from '@/common/components/icons/CheckIcon';
import InfoIcon from '@/common/components/icons/InfoIcon';
import WarningIcon from '@/common/components/icons/WarningIcon';
import SensorsKrxIconOff from '@/common/components/icons/SensorsKrxIconOff';

function NotificationIcon({ notification }: { notification: Notification }) {
  switch (notification.type) {
    case NotificationType.Success:
      return (
        <div className="rounded-full border-black border w-6 h-6 flex items-center justify-center">
          <CheckIcon size="sm" />
        </div>
      );
    case NotificationType.Info:
      return <InfoIcon />;
    case NotificationType.Warning:
      return <WarningIcon />;
    case NotificationType.GameConnectionError:
      return <SensorsKrxIconOff />;
    case NotificationType.RequestTimeout:
      return <ErrorIcon />;
    case NotificationType.Error:
      return <ErrorIcon />;
  }
}

function getAlertSpecificCSSClass(notification: Notification) {
  switch (notification.type) {
    case NotificationType.Info:
      return 'alert-info';
    case NotificationType.Success:
      return 'alert-success';
    case NotificationType.Warning:
    case NotificationType.GameConnectionError:
      return 'alert-warning';
    case NotificationType.RequestTimeout:
      return 'alert-error';
    case NotificationType.Error:
      return 'alert-error';
  }
}

function NotificationElement({
  notification,
  onDismiss,
}: {
  notification: Notification;
  onDismiss: () => void;
}) {
  return (
    <div
      className={classNames(
        'alert shadow-2xl min-w-96 max-w-lg items-start',
        getAlertSpecificCSSClass(notification)
      )}
    >
      <div className="flex text-xl">
        <NotificationIcon notification={notification} />
      </div>
      <div className="overflow-hidden">
        <h3 className="font-bold capitalize">{notification.type}</h3>
        <div className="text-xs whitespace-normal">{notification.message}</div>
        {notification.details && (
          <div className="mt-1 text-xs whitespace-normal opacity-80">{notification.details}</div>
        )}
      </div>
      <div className="flex-none">
        <button
          className="btn-circle btn btn-neutral btn-xs cursor-pointer"
          onClick={() => onDismiss()}
        >
          <XIcon />
        </button>
      </div>
    </div>
  );
}

export default function Notifications({ children }: { children?: React.ReactNode }) {
  const useNotificationStore = getNotificationsStore();

  const notifications = useNotificationStore((state) => state.notifications);
  const dismissNotification = useNotificationStore((state) => state.dismissNotification);

  const onDismiss = (notification: Notification) => dismissNotification(notification.id);

  useHotkeys(
    'esc',
    () => {
      if (!notifications || notifications.length === 0) {
        return;
      }
      onDismiss(notifications[0]);
    },
    {},
    [notifications]
  );

  return (
    <div className="absolute z-[999]">
      <TransitionGroup
        className={classNames({
          'toast toast-top top-9 toast-end': children != null || notifications.length > 0,
        })}
      >
        {children}
        {notifications.map((notification) => {
          const itemRef = createRef<HTMLDivElement>();
          const onDismissClicked = () => {
            onDismiss(notification);
          };

          return (
            <CSSTransition
              nodeRef={itemRef}
              key={notification.id}
              timeout={500}
              classNames="notifications__animated-notification"
            >
              <NotificationElement notification={notification} onDismiss={onDismissClicked} />
            </CSSTransition>
          );
        })}
      </TransitionGroup>
    </div>
  );
}
